/* ═══════════════════════════════════════════════════════════════
   Team widget — ONE simple roster, grouped by department column
   (Team Widget R2, 2026-10-02). Josh's verdict on the prior build:
   "so much information packed into it, yet it's telling me nothing"
   and it "doesn't match the other widgets" — utilization, forecast,
   department chip, and the per-row role select are all gone. A row is
   now just avatar + name + a PIC/PM badge when applicable; the "…"
   menu (quiet until hover/focus) replaces the role select with two
   explicit actions ("Make PIC" / "Make PM") plus Remove, same set the
   old select could reach (its only other value was 'member', hidden on
   PIC/PM rows).

   Rows no longer drill to the People page — "People is for admin
   stuff, Projects is for project stuff" (Josh). The same ruling removed
   the custom-widget row drill on the Team source (widgetSources.ts),
   the Forecast widget's member-row drill, and the `'team-member'` drill
   kind itself — nothing on a project dashboard jumps to People.
   ═══════════════════════════════════════════════════════════════ */
import React, { useMemo, useState } from 'react';
import { MoreHorizontal, Plus, Settings2 } from 'lucide-react';
import type { TeamMember, ProjectAssignment } from '../../../../shared/entities';
import type { Department } from '../../../../shared/entities';
import { Icon } from '../../../components/ui/Icon';
import { WidgetFrame, WidgetActionButton, WList } from '../../../components/widgets';
import type { WListRow, WListGroup } from '../../../components/widgets';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../../components/ui/context-menu';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { assignableTeamMembers } from './teamAssignable';
import { rem } from '../../../lib/rem';
import { sectionLabel } from './shared';
import { TeamPanel } from '../panels/TeamPanel';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

/** Minimum department-column width before the grid wraps to fewer
 *  columns — sized for avatar + name + a two-letter-wide badge. */
const TEAM_COL_MIN_REM = 15;

function initialsOf(name: string): string {
  return (name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface Entry {
  a: ProjectAssignment;
  m: TeamMember;
}

/** PIC first, then PM, then alphabetical by name — within one department
 *  column (and within "No department"). */
function sortEntries(list: Entry[], picId?: string, pmId?: string): Entry[] {
  return [...list].sort((x, y) => {
    const xPic = x.a.teamMemberId === picId;
    const yPic = y.a.teamMemberId === picId;
    if (xPic !== yPic) return xPic ? -1 : 1;
    const xPm = x.a.teamMemberId === pmId;
    const yPm = y.a.teamMemberId === pmId;
    if (xPm !== yPm) return xPm ? -1 : 1;
    return x.m.name.localeCompare(y.m.name);
  });
}

function TeamBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [manageOpen, setManageOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<ProjectAssignment | null>(null);
  const [addPersonId, setAddPersonId] = useState('');
  const assignments = ctx.assignments.data;

  const frameStatus =
    ctx.assignments.status === 'loading'
      ? 'loading'
      : ctx.assignments.status === 'error'
        ? 'error'
        : assignments.length === 0
          ? 'empty'
          : 'ready';

  const actions = ctx.can.assignTeam ? (
    <WidgetActionButton icon={Settings2} label="Manage team" onClick={() => setManageOpen(true)} />
  ) : undefined;

  const assignedIds = new Set(assignments.map((a) => a.teamMemberId));
  // Decision 11 — the "who may I assign" filter (shared with TeamPanel's
  // picker so neither surface offers a person the server will refuse).
  const available = assignableTeamMembers(ctx, assignedIds);

  const entries: Entry[] = useMemo(
    () =>
      assignments
        .map((a) => ({ a, m: ctx.members.find((mm) => mm.id === a.teamMemberId) }))
        .filter((x): x is Entry => !!x.m),
    [assignments, ctx.members]
  );

  // Group by department (members with no resolvable department land in a
  // last "No department" group); department columns in the department's
  // own sortOrder, falling back to alphabetical, departments with nobody
  // on the project are never shown.
  const groupedByDept = useMemo(() => {
    const byDept = new Map<string, Entry[]>();
    const noneKey = '__none__';
    for (const entry of entries) {
      const dept = entry.m.departmentId
        ? ctx.departments.find((d) => d.id === entry.m.departmentId)
        : undefined;
      const key = dept ? dept.id : noneKey;
      const list = byDept.get(key) || [];
      list.push(entry);
      byDept.set(key, list);
    }
    const ordered: { dept: Department | null; entries: Entry[] }[] = ctx.departments
      .filter((d) => byDept.has(d.id))
      .sort((x, y) => {
        const xo = x.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const yo = y.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (xo !== yo) return xo - yo;
        return x.name.localeCompare(y.name);
      })
      .map((dept) => ({ dept, entries: byDept.get(dept.id)! }));
    const none = byDept.get(noneKey);
    if (none && none.length) ordered.push({ dept: null, entries: none });
    return ordered;
  }, [entries, ctx.departments]);

  function buildRow(a: ProjectAssignment, m: TeamMember): WListRow {
    const isPic = a.teamMemberId === ctx.project.picId;
    const isPm = a.teamMemberId === ctx.project.pmId;
    // Role tone lives in the tint + border, never in the text color — a
    // colored label on a colored tint reads <4.5:1 in light theme (DESIGN.md
    // §7). Text is always `--tx` (body contrast, passes both themes).
    const roleBadge =
      isPic || isPm
        ? isPic
          ? {
              text: isPm ? 'PIC / PM' : 'PIC',
              bg: 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
              border: 'color-mix(in srgb, var(--color-warning) 45%, transparent)',
            }
          : {
              text: 'PM',
              bg: 'rgba(var(--acr),0.12)',
              border: 'rgba(var(--acr),0.45)',
            }
        : null;

    return {
      id: a.id,
      lead: (
        <span
          style={{
            width: rem(26),
            height: rem(26),
            borderRadius: '50%',
            background: 'var(--ac)',
            color: 'var(--color-text-inverse)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initialsOf(m.name)}
        </span>
      ),
      primary: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: rem(6), minWidth: 0 }}>
          <span className="truncate">{m.name}</span>
          {roleBadge && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                padding: `1px ${rem(5)}`,
                borderRadius: rem(3),
                background: roleBadge.bg,
                border: `1px solid ${roleBadge.border}`,
                color: 'var(--tx)',
                fontWeight: 700,
                letterSpacing: '.03em',
                flexShrink: 0,
              }}
            >
              {roleBadge.text}
            </span>
          )}
        </span>
      ),
      trailingReveal: true,
      trailing: ctx.can.assignTeam ? (
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <WidgetActionButton
                icon={MoreHorizontal}
                label={`${m.name} actions`}
                onClick={() => {}}
              />
            </ContextMenuTrigger>
            <ContextMenuContent
              align="end"
              sideOffset={4}
              className="min-w-[10rem] rounded-md border border-border bg-surface p-1 shadow-md"
            >
              {!isPic && (
                <ContextMenuItem
                  className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-text outline-none hover:bg-hover"
                  onSelect={() => void ctx.setAssignmentRole(a.id, 'pic')}
                >
                  Make PIC
                </ContextMenuItem>
              )}
              {!isPm && (
                <ContextMenuItem
                  className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-text outline-none hover:bg-hover"
                  onSelect={() => void ctx.setAssignmentRole(a.id, 'pm')}
                >
                  Make PM
                </ContextMenuItem>
              )}
              <ContextMenuSeparator className="my-1 h-px bg-border" />
              <ContextMenuItem
                className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-danger outline-none hover:bg-hover"
                onSelect={() => setPendingRemove(a)}
              >
                Remove from project
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        </span>
      ) : undefined,
    };
  }

  const groups: WListGroup[] = groupedByDept.map(({ dept, entries: deptEntries }) => ({
    key: dept ? dept.id : '__none__',
    label: <div style={sectionLabel}>{dept ? dept.name : 'No department'}</div>,
    rows: sortEntries(deptEntries, ctx.project.picId, ctx.project.pmId).map(({ a, m }) =>
      buildRow(a, m)
    ),
  }));

  return (
    <>
      <WidgetFrame
        title="Team"
        required
        form="list"
        status={frameStatus}
        error={{
          message: ctx.assignments.error || 'Could not load team.',
          onRetry: () => void ctx.assignments.reload(),
        }}
        empty={{
          title: 'No team assigned yet',
          action: ctx.can.assignTeam
            ? { label: 'Assign team', onClick: () => setManageOpen(true) }
            : undefined,
        }}
        actions={actions}
      >
        <WList
          groups={groups}
          minColRem={TEAM_COL_MIN_REM}
          footer={
            ctx.can.assignTeam ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  minHeight: rem(32),
                  padding: '0 var(--space-2)',
                }}
              >
                <Icon
                  icon={Plus}
                  size="md"
                  aria-hidden="true"
                  style={{ color: 'var(--tm)', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect
                    value={addPersonId}
                    options={available.map((m) => ({ value: m.id, label: m.name }))}
                    onChange={(v) => {
                      setAddPersonId('');
                      if (v) void ctx.assignMember(v, 'member');
                    }}
                    placeholder="Add person…"
                    searchPlaceholder="Search members…"
                    noResultsText="No one on your team to add"
                  />
                </div>
              </div>
            ) : undefined
          }
        />
      </WidgetFrame>
      <TeamPanel ctx={ctx} open={manageOpen} onClose={() => setManageOpen(false)} />
      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(o) => !o && setPendingRemove(null)}
        title="Remove from project?"
        description="This member loses access to this project's assignment. Their logged time is unaffected."
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          if (!pendingRemove) return;
          await ctx.removeAssignment(pendingRemove.id);
          setPendingRemove(null);
        }}
      />
    </>
  );
}

export const teamWidget: ProjectWidgetDef = {
  id: 'team',
  title: 'Team',
  category: 'people',
  required: true,
  inputSurface: true,
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  render: (ctx) => <TeamBody ctx={ctx} />,
};
